import { Link } from 'react-router-dom'
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'

export default function AcceptableUse() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3 text-sm text-blue-200">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
          <Link className="hover:text-white" to="/login">Back to secure access</Link>
        </div>
        <div className="mb-10 flex items-center gap-3 text-blue-200">
          <ShieldCheckIcon className="h-10 w-10" aria-hidden />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-300/80">Acceptable Use & Zero-Trust Charter</p>
            <h1 className="text-3xl font-semibold text-white">CyberSec Responsible Use Policy</h1>
          </div>
        </div>
        <div className="space-y-6 text-sm leading-relaxed text-gray-300 sm:text-base">
          <p>
            CyberSec operates as a zero-trust training environment. Every login is verified, every action is attributed to an
            authenticated participant, and monitoring is in place to evidence compliant behaviour for both academic cohorts and
            professional security teams. You may only operate on designated demo targets or organisation-owned systems that you can
            prove control over via the built-in verification workflow. By accessing the workspace you agree to honour the safeguards
            described below.
          </p>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-white">1. Scope of Access</h2>
            <p>
              Use of the platform is restricted to sanctioned research, training, and security teams operating under documented authorisation.
              Only designated training targets or assets whose ownership you can verify through the platform may be exercised. External systems,
              production infrastructure, or assets without written approval are strictly out of bounds.
            </p>
            <p>
              Target verification requires evidence of control (for example hosting an HTTP proof file or publishing a DNS TXT record). Until
              verification succeeds, live tooling remains blocked for that asset.
            </p>
          </section>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-white">2. Monitoring & Attribution</h2>
            <p>
              Sessions are logged with identity, timestamps, and activity summaries. Monitoring supports oversight, audit readiness, and
              incident response. Deliberate attempts to evade attribution, tamper with logging, or mask identity are prohibited.
            </p>
          </section>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-white">3. Data Handling</h2>
            <p>
              Do not upload personal, regulated, or confidential information. Evidence generated within the environment must follow
              institutional retention and privacy policies. Purge downloadable artefacts once assessment or research review concludes.
            </p>
          </section>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-white">4. Ethical Conduct</h2>
            <p>
              Exercises simulate adversarial behaviour in a contained sandbox. Use the platform to observe and mitigate security risk,
              not to rehearse unsanctioned intrusion. Any suspicious or accidental contact with non-approved systems must be reported to
              the programme lead immediately.
            </p>
          </section>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-white">5. Support & Escalation</h2>
            <p>
              Questions about acceptable use, policy interpretation, or incident handling should be directed to the programme lead or security
              administrator at <a className="text-blue-200 underline hover:text-white" href="mailto:theregalstarlite@gmail.com">theregalstarlite@gmail.com</a>.
              Continued access is contingent on acknowledging and following this policy.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
